// Copyright (c) StrangeBee 2023

import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { CustomEventDrawer } from '@Organisms/Timeline/CustomEventDrawer/CustomEventDrawer';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import { TimelineEventList } from '@Organisms/Timeline/eventList';
import { useListAllUsers } from '@Queries/userQueries';
import React, { ReactElement } from 'react';
import { CustomEventType } from 'thehive-sdk';

import './CustomEventContent.scss';

export const CustomEventContent = (props: DisplayEventProps): ReactElement | null => {
    const i = useLocalMessage();
    const { data: userList = [] } = useListAllUsers();
    const { currentEvent, eventList, refetch, hasPermissionEvent = false } = props;
    const data = props.currentEvent.details?.customEvent;
    const { visible, setVisible } = useDrawer<CustomEventType>({});

    const { _createdBy = '', date = '', endDate = '', title = '' } = data || {};

    return (
        <EventCard {...props}>
            <div className="custom-event-content card__content">
                <div className="custom-event-content__header card__header">
                    <div className="custom-event-content__title">
                        {title}
                        {eventList && hasPermissionEvent ? (
                            <DeleteButton
                                onConfirm={() => {
                                    if (currentEvent) {
                                        eventList?.onDelete(currentEvent.entityId);
                                    }
                                }}
                                label="Delete this event"
                            />
                        ) : null}
                    </div>
                </div>

                {_createdBy && (
                    <THEditable
                        name="_createdBy"
                        className="card-info__user"
                        initialValues={{ _createdBy }}
                        label={i('common.created-by')}
                    >
                        <THSelect disabled borderless>
                            {userList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THEditable>
                )}

                {date && (
                    <THEditable
                        name="date"
                        className="card-info__date"
                        initialValues={{ date }}
                        label={i('common.start-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {endDate && (
                    <THEditable
                        name="endDate"
                        className="card-info__date"
                        initialValues={{ endDate }}
                        label={i('common.end-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {data && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {data && (
                    <CustomEventDrawer
                        onVisibleChange={setVisible}
                        visible={visible}
                        data={data}
                        eventList={TimelineEventList({
                            controller: client.TimelineController,
                            local: i,
                            onSuccess: (message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                            onError: handleNotificationError,
                        })}
                        hasPermission={hasPermissionEvent}
                    />
                )}
            </div>
        </EventCard>
    );
};
